import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import Logo from "@/components/Logo";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

const HERO =
  // Event-neutral clinic interior (same shot as the admin login) — swap in the
  // Diabetes Day visual when design supplies one.
  "https://images.unsplash.com/photo-1787496994323-59ac5cff09f9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBjbGluaWMlMjBpbnRlcmlvcnxlbnwwfHx8fDE3ODk1NTY2NDN8MA&ixlib=rb-4.1.0&q=85";

export default function MRLogin() {
  const [empCode, setEmpCode] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(empCode.trim(), password.trim());
      if (u.role !== "mr") {
        toast.error("This is not a manager account. Use the Admin panel.");
        return;
      }
      toast.success(`Welcome, ${u.name || u.emp_code}`);
      navigate("/mr");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="relative hidden lg:block overflow-hidden">
        <img src={HERO} alt="Clinic" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#0B5351]/85" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <Logo onDark />
            <span className="font-heading font-extrabold text-xl tracking-tight">Blue Cross</span>
          </div>
          <div className="max-w-md">
            <p className="text-xs uppercase tracking-[0.3em] text-white/60 mb-4">Manager Portal</p>
            <h1 className="font-heading text-5xl font-black leading-[1.05] tracking-tighter">
              World Diabetes Day Patient Awareness Initiative
            </h1>
          </div>
          <span />
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm animate-fade-up">
          <Logo className="lg:hidden h-12 w-12 mb-6" />
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-2">Manager Login</p>
          <h2 className="font-heading text-3xl font-black tracking-tighter text-slate-900 mb-8">Sign in with EMP Code</h2>

          <form onSubmit={submit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="emp">EMP Code</Label>
              <Input
                id="emp"
                data-testid="mr-empcode-input"
                value={empCode}
                onChange={(e) => setEmpCode(e.target.value)}
                placeholder="e.g. EMP1024"
                className="h-12 bg-slate-50"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pwd">Password</Label>
              <Input
                id="pwd"
                data-testid="mr-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Same as your EMP Code"
                className="h-12 bg-slate-50"
                required
              />
              <p className="text-xs text-slate-500">Your password is the same as your EMP Code.</p>
            </div>
            <Button
              type="submit"
              data-testid="mr-login-submit"
              disabled={loading}
              className="w-full h-12 rounded-xl bg-[#0B5351] hover:bg-[#094241] text-white font-semibold"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Sign In"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
